<template>
    <base-dialog :loading="loading" :value="dialog" :width="460" :title="$t('common.create_chat')"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="chat-form">
            <select-option-students no-list :value="optionChat" :features="chatFeatures" @change="onTabChange" />

            <div class="form-group">
                <span class="field-label">{{ selectLabel }}</span>
                <kimsa-combobox searchable :placeholder="$t('common.select_placeholder')" dense :items="options"
                    @change="setOption" :value="option" item-value="objectID" />
            </div>

            <div class="form-group" v-if="isTeamChats">
                <span class="field-label">{{ $t('common.subgroup') }}</span>
                <kimsa-combobox searchable :readonly="!option" :placeholder="$t('common.select_placeholder')" dense
                    :items="subOptions" @change="setSubOption" :value="subOption" item-value="objectID" />
            </div>

            <div class="form-group">
                <span class="field-label">{{ $t('common.initial_message') }}
                    <span class="optional">({{ $t('common.optional').toLowerCase() }})</span>
                </span>
                <kimsa-textarea-field :rows="3" :placeholder="$t('common.write_here')" @change="message = $event"
                    :value="message" force-value />
            </div>
        </div>

        <template v-slot:footer>
            <dialog-actions :confirm-label="$t('common.create_chat')" :disabled="!valid" :loading="loading"
                @cancel="hide" @confirm="save" />
        </template>
    </base-dialog>
</template>

<script>
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import { mapMutations, mapState } from "vuex";
import { UserIndex } from "@/utils/Algolia";
import { db, Timestamp } from "@/utils/Firebase";
import { findChat } from "@/utils/Messages";
import KimsaCombobox from '../common/inputs/KimsaCombobox.vue';
import KimsaTextareaField from "@/components/common/inputs/KimsaTextareaField";
import SelectOptionStudents from './SelectOptionStudents.vue';
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
export default {
    name: "CreateChatDialog",
    components: { KimsaCombobox, KimsaTextareaField, SelectOptionStudents, BaseDialog, DialogActions },
    props: {
        isSecondDialog: {
            type: Boolean,
            default: false,
        },
        chatType: {
            type: String,
            default: 'student'
        }
    },
    data() {
        return {
            dialog: false,
            loading: false,
            message: '',
            options: [],
            option: null,
            subOption: null,
            optionChat: 'student',
        }
    },
    computed: {
        ...mapState(['groups', 'teams']),
        chatFeatures() {
            return { group: 'groupChat', team: 'groupChat' }
        },
        valid(vm = this) {
            if (vm.isTeamChats) return !!(vm.option && vm.subOption)
            return !!vm.option
        },
        coachId(vm = this) {
            return vm.currentUser?.uid || null
        },
        collectionName(vm = this) {
            return vm.isStudentChats ? 'privateChats' : 'groupPrivateChats'
        },
        isStudentChats(vm = this) {
            return vm.optionChat === 'student'
        },
        isTeamChats(vm = this) {
            return vm.optionChat === 'team'
        },
        selectLabel(vm = this) {
            return `${vm.$t('common.select_placeholder')} ${vm.$t(`common.${vm.optionChat}`).toLowerCase()}`
        },
        subOptions(vm = this) {
            return vm.option?.groups?.map(group => ({
                id: group.id,
                name: group.name,
                teamId: vm.option.id,
                teamName: vm.option.name,
            })) || []
        }
    },
    methods: {
        show() {
            const vm = this
            vm.optionChat = vm.chatType || 'student'
            vm.dialog = true
            vm.loadOptions()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.option = null
            vm.subOption = null
            vm.message = ''
        },
        onTabChange(value) {
            const vm = this
            vm.optionChat = value
            vm.option = null
            vm.subOption = null
            if (vm.dialog) vm.loadOptions()
        },
        async loadOptions() {
            const vm = this;
            if (vm.isStudentChats) {
                let filter = `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`
                let response = await UserIndex.search('', { filters: filter, hitsPerPage: 1000 })
                vm.options = response.hits
            } else if (vm.chatType === 'group') {
                vm.options = vm.groups
            } else {
                vm.options = vm.teams
            }
        },
        setOption(option) {
            const vm = this;
            vm.option = option?.name ? option : vm.options?.find(op => op.name?.toLowerCase() === option?.toLowerCase()) || null
        },
        setSubOption(subOption) {
            const vm = this;
            vm.subOption = subOption?.name ? subOption : vm.subOptions?.find(sop => sop.name?.toLowerCase() === subOption?.toLowerCase()) || null
        },
        async addMessage(collectionName, chatId) {
            const vm = this
            let messagePayload = {
                message: vm.message,
                from: vm.coachId,
                read: true,
                timestamp: Timestamp.now(),
                type: 'text',
            }
            await db.collection(collectionName).doc(chatId).collection('messages').add(messagePayload)
            await db.collection(collectionName).doc(chatId).update({ lastMessage: messagePayload })
        },
        async findStudentChat(studentId) {
            const vm = this
            const chat = await findChat('privateChats', 'coach_student', vm.coachId, studentId)
            return chat?.id || null
        },
        async save() {
            const vm = this
            await vm.withLoading(async () => {
                let client = vm.subOption || vm.option
                let chatId

                if (vm.isStudentChats) {
                    chatId = await vm.findStudentChat(client.objectID)
                    if (!chatId) {
                        const newChat = await db.collection('privateChats').add({
                            type: 'coach_student',
                            participants: [vm.coachId, client.objectID],
                        })
                        chatId = newChat.id
                    }
                } else {
                    const newChat = await db.collection('groupPrivateChats').add({
                        type: vm.optionChat,
                        teamId: client?.teamId || null,
                        groupId: client?.id ?? null,
                        coachId: vm.coachId,
                    })
                    chatId = newChat.id
                }

                if (vm.message) await vm.addMessage(vm.collectionName, chatId)

                vm.hide()
                vm.$emit('success', { chatId, client })
            })
        }
    },
}
</script>

<style scoped>
.chat-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.optional {
    font-weight: 400;
    color: var(--v-txtMuted-base);
}
</style>
